import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { Loader2, Play, SquareTerminal } from 'lucide-react';
import { toast } from 'sonner';
import { STAFF_PROXY_TESTING_COMMAND_EXECUTIONS } from '@staff-app/graphql-client';
import {
  Badge,
  Button,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@staff-app/ui';
import { safeDistanceToNow, safeJsonParse } from '@/lib/safe-format';
import { commandLabel } from '../lib/activity-label';
import { errorText, EXECUTE_PRESETS } from '../lib/device-commands';
import type { CommandExecutionRow } from '../lib/types';
import {
  TestingConfirm,
  TestingDialogFrame,
  type TestingCommandDialogProps,
} from './TestingDialogFrame';
import { useDrawerScopeVars } from '../../../lib/drawer-mode';

/** scio3 polls the history every 5s while the dialog is open; here it stops after a bounded window. */
export const EXECUTE_HISTORY_POLL_MS = 5_000;
export const EXECUTE_HISTORY_POLL_MAX_MS = 5 * 60_000;
const EXECUTE_HISTORY_LIMIT = 10;

const STATUS: Record<number, { label: string; variant: 'success' | 'destructive' | 'warning' | 'secondary' }> = {
  0: { label: 'Submitted', variant: 'warning' },
  1: { label: 'Received', variant: 'secondary' },
  2: { label: 'Executed', variant: 'success' },
  3: { label: 'Cancelled', variant: 'secondary' },
  4: { label: 'Rejected', variant: 'destructive' },
  5: { label: 'Failed', variant: 'destructive' },
};

/**
 * More › Execute Remote Commands — the single-device slice of scio3 `ExecuteCommandDialog`:
 * `staffProxyTestingExecuteRemoteCommand(deviceId, command)` (confirmed first — a free-form command on
 * a real device), then the recent results from `staffProxyTestingCommandExecutions`, polled every 5s
 * while open for at most 5 minutes after the last send. No tag / multi-device targeting.
 */
export function TestingExecuteCommandDialog({
  open,
  detail,
  deviceName,
  commands,
  onClose,
  onDone,
}: TestingCommandDialogProps) {
  const [command, setCommand] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [polling, setPolling] = useState(true);
  const [pollEpoch, setPollEpoch] = useState(0);

  useEffect(() => {
    if (!open || !polling) return;
    const t = setTimeout(() => setPolling(false), EXECUTE_HISTORY_POLL_MAX_MS);
    return () => clearTimeout(t);
  }, [open, polling, pollEpoch]);

  const scope = useDrawerScopeVars();
  const { data, loading, refetch } = useQuery<{ staffProxyTestingCommandExecutions: string }>(
    STAFF_PROXY_TESTING_COMMAND_EXECUTIONS,
    {
      variables: { deviceId: detail._id, first: EXECUTE_HISTORY_LIMIT, ...scope },
      skip: !open,
      fetchPolicy: 'network-only',
      pollInterval: open && polling ? EXECUTE_HISTORY_POLL_MS : 0,
    },
  );
  const rows = useMemo(
    () => safeJsonParse<CommandExecutionRow[]>(data?.staffProxyTestingCommandExecutions, []) ?? [],
    [data],
  );

  const run = async () => {
    setConfirming(false);
    const cmd = command.trim();
    if (!cmd) return;
    setSending(true);
    setError(null);
    try {
      await commands.executeRemoteCommand(cmd);
      toast.success('Command submitted');
      setCommand('');
      setPolling(true);
      setPollEpoch((n) => n + 1);
      void refetch();
      onDone();
    } catch (err) {
      const message = errorText(err, 'Failed to execute command');
      setError(message);
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  return (
    <TestingDialogFrame
      open={open}
      onClose={onClose}
      title={'Execute Remote Commands'}
      icon={SquareTerminal}
      testId="testing-execute-dialog"
      size="xl"
      closeLabel={'Close'}
      footer={
        <Button
          variant="destructive"
          onClick={() => setConfirming(true)}
          disabled={!command.trim() || sending}
          disabledTooltip={!command.trim() && !sending ? 'Enter a command' : undefined}
          data-testid="testing-execute-run"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Play className="h-4 w-4" />}
          {'Execute'}
        </Button>
      }
    >
      <div className="space-y-2">
        <Label htmlFor="testing-execute-command">{'Command'}</Label>
        <Input
          id="testing-execute-command"
          className="font-mono"
          placeholder={'Enter or select command'}
          value={command}
          onChange={(e) => setCommand(e.target.value)}
        />
        <div className="flex flex-wrap gap-1.5">
          {EXECUTE_PRESETS.map((p) => (
            <Button
              key={p.value}
              variant="outline"
              size="xs"
              onClick={() => setCommand(p.value)}
              data-testid={`testing-execute-preset-${p.value}`}
            >
              {p.label}
            </Button>
          ))}
        </div>
        {error ? <p className="text-xs text-destructive">{error}</p> : null}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
          <span>{'Recent results'}</span>
          <span className="font-normal" data-testid="testing-execute-polling">
            {polling ? 'Refreshing every 5s' : 'Auto-refresh stopped'}
          </span>
        </div>
        {loading && rows.length === 0 ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{'No command history'}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{'Command'}</TableHead>
                <TableHead>{'Status'}</TableHead>
                <TableHead>{'Result'}</TableHead>
                <TableHead>{'When'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const status = row.status != null ? STATUS[row.status] : undefined;
                const at = row.lastUpdatedDate ?? row.createdAt;
                const shown = row.command === 'executeCommands' && row.value ? row.value : commandLabel(row.command);
                return (
                  <TableRow key={row._id} data-testid="testing-execute-row">
                    <TableCell className="max-w-[14rem] truncate font-mono text-xs" title={row.value ?? undefined}>
                      {shown}
                    </TableCell>
                    <TableCell>
                      {status ? <Badge variant={status.variant}>{status.label}</Badge> : '—'}
                    </TableCell>
                    <TableCell className="max-w-[18rem] text-xs text-muted-foreground">
                      {row.result ? (
                        <details>
                          <summary className="cursor-pointer select-none truncate">
                            {row.result.length > 60 ? `${row.result.slice(0, 60)}…` : row.result}
                          </summary>
                          <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-all rounded bg-muted p-2 font-mono text-[11px]">
                            {row.result}
                          </pre>
                        </details>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {safeDistanceToNow(at, { addSuffix: true }, '—')}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <TestingConfirm
        open={confirming}
        title={'Execute remote command'}
        description={
          <>
            {`Run this on ${deviceName}?`}
            <code className="mt-2 block rounded bg-muted p-2 font-mono text-xs">{command.trim()}</code>
          </>
        }
        confirmLabel={'Execute'}
        onConfirm={() => void run()}
        onCancel={() => setConfirming(false)}
      />
    </TestingDialogFrame>
  );
}
